import { useState } from 'react';
import { withHistory } from 'slate-history';
import { v4 as uuidv4 } from 'uuid';
import { withReact } from 'slate-react';
import { createEditor, Transforms, Node, Range, Element, Editor as SlateEditor } from 'slate';
import withDefaultRenders from 'slate/extensions/default-renderers';
import { compose } from 'ramda';
import { CustomEditor, CustomElement } from 'slate/types/slate';
import { stripTagMarkers } from 'slate/extensions/tag/utils/paste';

type EditorPlugin = (editor: CustomEditor) => CustomEditor;

export const useEditor = (extensions: EditorPlugin[], _readOnly?: boolean) => {
  const [editor] = useState<CustomEditor>(() => {
    const withExtensions = (compose as (...fns: EditorPlugin[]) => EditorPlugin)(...extensions, withDefaultRenders, withHistory, withReact);
    const editor = createEditor() as CustomEditor;
    editor.deleteFragment = () => {
      const selection = editor.selection;
      if (!selection || Range.isCollapsed(selection)) {
        return;
      }
      SlateEditor.withoutNormalizing(editor, () => {
        Array.from(
          Node.texts(editor, {
            from: selection.anchor.path,
            to: selection.focus.path,
          })
        )
          .reverse()
          .map(([node, path]) =>
            Range.intersection(
              {
                anchor: { path, offset: 0 },
                focus: { path, offset: node.text.length },
              },
              selection
            )
          )
          .forEach((range) => {
            if (!range || Range.isCollapsed(range)) {
              return;
            }
            Transforms.delete(editor, { at: range });
          });
      });
    };

    const { normalizeNode } = editor;
    editor.normalizeNode = (entry) => {
      const [node] = entry;
      if (!SlateEditor.isEditor(node)) {
        normalizeNode(entry);
        return;
      }

      const descendants = Node.descendants(node);

      const used = new Set<string>();
      for (const [_node, _path] of descendants) {
        const nodeUuid = Element.isElement(_node) ? _node.uuid : undefined;
        if (!nodeUuid) {
          continue;
        }
        if (used.has(nodeUuid)) {
          Transforms.setNodes(editor, { uuid: uuidv4() } as Partial<CustomElement>, { at: _path });
        }
        used.add(nodeUuid);
      }
    };

    preventDeletionOverMultiplePages(editor);
    preventOperationsOverMultipleLayouts(editor);

    const customEditor = withExtensions(editor) as CustomEditor;
    const { insertData, insertFragment } = customEditor;

    customEditor.insertFragment = (fragment, options) => insertFragment(stripTagMarkers(fragment), options);

    // Paste HTML as Text into document
    customEditor.insertData = (data: DataTransfer) => {
      if (data) {
        const html = data.getData('text/html');
        if (html) {
          // TODO: Hantera genom att behålla utvalda element och formatering
          const text = data.getData('text/plain');

          customEditor.insertText(text);
          return;
        }
      }

      insertData(data);
    };
    return customEditor;
  });
  return editor;
};

const preventDeletionOverMultiplePages = (editor: CustomEditor) => {
  const { insertText, deleteFragment, insertBreak } = editor;

  editor.insertText = (entry) => {
    if (isMultiPageSelection(editor)) {
      console.log('Text insertion prevented, as text selection spans multiple pages');
    } else {
      insertText(entry);
    }
  };

  editor.deleteFragment = (entry) => {
    if (isMultiPageSelection(editor)) {
      console.log('Text deletion prevented, as text selection spans multiple pages');
    } else {
      deleteFragment(entry);
    }
  };

  editor.insertBreak = () => {
    if (isMultiPageSelection(editor)) {
      console.log('Line break prevented, as text selection spans multiple pages');
    } else {
      insertBreak();
    }
  };
};

const isMultiPageSelection = (editor: CustomEditor) => {
  const anchorPage = editor.selection?.anchor.path[0];
  const focusPage = editor.selection?.focus.path[0];
  return anchorPage !== focusPage;
};

const isMultiLayoutSelection = (editor: CustomEditor) => {
  if (!editor.selection || Range.isCollapsed(editor.selection)) return false;

  const anchorLayoutLeaf = SlateEditor.above(editor, {
    at: editor.selection.anchor,
    match: (n: Node) => Element.isElement(n) && n.type === 'layout' && !!n.isLayoutLeaf,
  });

  const focusLayoutLeaf = SlateEditor.above(editor, {
    at: editor.selection.focus,
    match: (n: Node) => Element.isElement(n) && n.type === 'layout' && !!n.isLayoutLeaf,
  });

  if (!anchorLayoutLeaf || !focusLayoutLeaf) return false;
  return anchorLayoutLeaf[0] !== focusLayoutLeaf[0];
};

const preventOperationsOverMultipleLayouts = (editor: CustomEditor) => {
  const { insertText, deleteFragment, insertBreak } = editor;

  editor.insertText = (text) => {
    if (isMultiLayoutSelection(editor)) {
      deleteFragment();
      Transforms.collapse(editor, { edge: 'anchor' });
    }
    insertText(text);
  };

  editor.deleteFragment = (direction) => {
    const selection = editor.selection;
    if (selection && isMultiLayoutSelection(editor)) {
      // Use range-based deletion to clear text without merging layout structure
      SlateEditor.withoutNormalizing(editor, () => {
        Array.from(
          Node.texts(editor, {
            from: selection.anchor.path,
            to: selection.focus.path,
          })
        )
          .reverse()
          .map(([node, path]) =>
            Range.intersection(
              {
                anchor: { path, offset: 0 },
                focus: { path, offset: node.text.length },
              },
              selection
            )
          )
          .forEach((range) => {
            if (!range || Range.isCollapsed(range)) {
              return;
            }
            Transforms.delete(editor, { at: range });
          });
      });
      Transforms.collapse(editor, { edge: 'anchor' });
      return;
    }
    deleteFragment(direction);
  };

  editor.insertBreak = () => {
    if (isMultiLayoutSelection(editor)) {
      editor.deleteFragment();
      return;
    }
    insertBreak();
  };
};
